// Fonte única do contrato de roteamento das editorias (CT UP, CT Eletro, CT Auto).
// Antes destes dados estarem centralizados, o mapeamento slug→rota vivia espalhado
// em quatro pontos (LEGACY_EDITORIA_SLUGS e MONOLITH_SLUG_OVERRIDES na página de
// categorias, EDITORIAL_PUBLIC_ROUTE e o campo `route` no FeaturedEditorialCategory),
// com risco de divergência entre SSR, render e navegação. Tudo que descreve "qual
// slug público, qual slug do monolito e como o bloco da home é buscado" nasce aqui.
//
// Fronteira: este módulo cuida só de slug/rota (dados puros, sem React). Cor e logo
// (apresentação) permanecem em `editorialCategoryProps` no FeaturedEditorialCategory.

// Variant de tema — identifica a editoria para cor, logo e regras de rota.
export type EditorialVariant = 'ctup' | 'eletro' | 'ctauto';

export interface EditorialRoute {
  // Variant de tema (cor/logo) associado a esta editoria.
  variant: EditorialVariant;
  // Slug da URL pública no Fawkes (ex.: `ct-eletro` → /ct-eletro/).
  slugPublic: string;
  // Slug que o monolito conhece na rota de categoria (ex.: `eletro`).
  // Igual ao slugPublic quando o monolito e o Fawkes usam o mesmo nome.
  slugMonolith: string;
  // Slugs legados ainda aceitos na entrada (ex.: `cteletro` antes da renomeação).
  slugAliases: string[];
  // Tipo de rota do bloco da home no monolito: `id` usa identificador numérico,
  // `categoria` usa o slug de categoria.
  routeType: 'id' | 'categoria';
  // Identificador/slug consumido pelo bloco da home no monolito.
  homeRoute: string;
}

export const EDITORIAL_ROUTES: EditorialRoute[] = [
  {
    variant: 'ctup',
    slugPublic: 'ctup',
    slugMonolith: 'ctup',
    slugAliases: [],
    routeType: 'id',
    homeRoute: '155778?merge=155790,155842',
  },
  {
    variant: 'eletro',
    slugPublic: 'ct-eletro',
    slugMonolith: 'eletro',
    slugAliases: ['cteletro'],
    routeType: 'categoria',
    homeRoute: 'eletro',
  },
  {
    variant: 'ctauto',
    slugPublic: 'ctauto',
    slugMonolith: 'ctauto',
    slugAliases: [],
    routeType: 'categoria',
    homeRoute: 'ctauto',
  },
];

// Busca a editoria pelo variant de tema.
export const findEditorialByVariant = (variant: EditorialVariant): EditorialRoute =>
  EDITORIAL_ROUTES.find(route => route.variant === variant) as EditorialRoute;

// Busca a editoria por um slug de entrada (URL pública ou alias legado).
export const findEditorialByPublicSlug = (slug: string): EditorialRoute | undefined =>
  EDITORIAL_ROUTES.find(route => route.slugPublic === slug || route.slugAliases.includes(slug));

// Busca a editoria quando o slug bruto coincide exatamente com o nome do variant
// (ctup/eletro/ctauto). Preserva o comportamento histórico da página de categorias,
// que só tratava esses três slugs como editoria de tema — sem capturar categorias
// legadas do CT UP como `hardware`/`games`.
export const findEditorialByExactVariantSlug = (slug: string): EditorialRoute | undefined =>
  EDITORIAL_ROUTES.find(route => route.variant === slug);

// Slugs de entrada que devem rotear direto para a editoria (merge monolito + Directus):
// os slugs públicos + aliases legados. Note que o slug do monolito (`eletro`) fica de
// fora de propósito — /eletro/ continua sendo categoria pura.
export const LEGACY_EDITORIA_SLUGS: string[] = EDITORIAL_ROUTES.flatMap(route => [route.slugPublic, ...route.slugAliases]);

// Traduz um slug público do Fawkes para o slug que o monolito conhece.
// Retorna o próprio slug quando não há tradução (categoria comum).
export const monolithSlugFor = (slug: string): string => findEditorialByPublicSlug(slug)?.slugMonolith ?? slug;

// Indica se o slug público difere do slug do monolito (ex.: ct-eletro → eletro).
export const hasMonolithOverride = (slug: string): boolean => {
  const route = findEditorialByPublicSlug(slug);
  return Boolean(route && route.slugMonolith !== slug);
};

// URL pública (/ctup/, /ct-eletro/, /ctauto/) a partir do variant de tema.
export const publicRouteFor = (variant: EditorialVariant): string => findEditorialByVariant(variant).slugPublic;
